import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Spinner } from '@/components/ui';
import { useAuth } from '@/features/auth/useAuth';

interface GuardProps {
  children: ReactNode;
}

/**
 * Blocks anonymous visitors. Remembers the destination so sign-in can send them
 * back afterwards — otherwise every protected deep link dumps people on `/`.
 */
export function RequireAuth({ children }: GuardProps) {
  const { status, isAuthenticated } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" label="Checking session" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  return children;
}

/**
 * Blocks signed-in users from auth forms.
 *
 * Without this, a session cookie that outlives a tab would leave someone staring
 * at a sign-in form they can no longer usefully submit.
 */
export function PublicOnly({ children }: GuardProps) {
  const { status, isAuthenticated } = useAuth();

  if (status === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" label="Checking session" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}
